import { useState, useEffect } from "react";
import axios from "axios";
import { NEXT_PUBLIC_API_URL } from "./config";
import "./App.css";

const tiers = [
  { label: "Tier 1", value: "tier1" },
  { label: "Tier 2", value: "tier2" },
  { label: "Tier 3", value: "tier3" },
  { label: "Tier 4", value: "tier4" },
];

function App() {
  const [knife, setKnife] = useState("");
  const [finish, setFinish] = useState("");
  const [tier, setTier] = useState("");
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [flippedItems, setFlippedItems] = useState(new Set());

  // New modal states
  const [modalStep, setModalStep] = useState(1);
  const [userApiKey, setUserApiKey] = useState("");
  const [apiKeyError, setApiKeyError] = useState("");

  // New filtering states
  const [availableOptions, setAvailableOptions] = useState(null);
  const [availableFinishes, setAvailableFinishes] = useState([]);
  const [availableTiers, setAvailableTiers] = useState([]);

  useEffect(() => {
    // Show modal step 1 on load
    setModalStep(1);

    // Fetch available options
    fetchAvailableOptions();
  }, []);

  const fetchAvailableOptions = async () => {
    try {
      const response = await axios.get(
        `${NEXT_PUBLIC_API_URL}/available-options`
      );
      setAvailableOptions(response.data);
    } catch (error) {
      console.error("Failed to fetch available options:", error);
    }
  };

  // Update available finishes when knife changes
  useEffect(() => {
    if (availableOptions && knife) {
      const finishesForKnife = availableOptions.finishes_by_knife[knife] || [];
      setAvailableFinishes(finishesForKnife);

      // Reset finish and tier if current selection is no longer valid
      if (!finishesForKnife.includes(finish)) {
        setFinish("");
        setTier("");
      }
    } else {
      setAvailableFinishes([]);
      setFinish("");
      setTier("");
    }
  }, [knife, availableOptions, finish]);

  // Update available tiers when knife or finish changes
  useEffect(() => {
    if (availableOptions && knife && finish) {
      const key = `${knife}_${finish}`;
      const combinationData = availableOptions.tiers_by_combination[key];

      if (combinationData) {
        const tiersForCombination = combinationData.available_tiers.map(
          (tierKey) => {
            const tierObj = tiers.find((t) => t.value === tierKey);
            return tierObj || { label: tierKey, value: tierKey };
          }
        );
        setAvailableTiers(tiersForCombination);

        // Reset tier if current selection is no longer valid
        if (!combinationData.available_tiers.includes(tier)) {
          setTier("");
        }
      } else {
        setAvailableTiers([]);
        setTier("");
      }
    } else {
      setAvailableTiers([]);
      setTier("");
    }
  }, [knife, finish, availableOptions, tier]);

  const handleCloseApiKeyModal = () => {
    if (!userApiKey.trim()) {
      setApiKeyError("API key is required.");
      return;
    }
    setApiKeyError("");
    setModalStep(0); // close modal
    // You can also store/use the API key here if needed
  };

  const toggleFlip = (itemId) => {
    setFlippedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const fetchPatterns = async () => {
    if (!knife || !finish || !tier) {
      setError("Please select all fields before searching.");
      return;
    }

    setHasSearched(true);
    setLoading(true);
    setError(null);
    setNoResults(false);
    setPatterns([]);
    setFlippedItems(new Set()); // Reset flip states when new search

    try {
      const maxFloat = 0.07; // Same as backend default

      // Get the def_index and paint_index for this combination
      if (!availableOptions) {
        setError("Available options not loaded. Please refresh the page.");
        setLoading(false);
        return;
      }

      const key = `${knife}_${finish}`;
      const combinationData = availableOptions.tiers_by_combination[key];

      if (!combinationData) {
        setError("Invalid knife/finish combination.");
        setLoading(false);
        return;
      }

      const paintIndex = combinationData.api_paint_index;

      const { data } = await axios.get(
        `${NEXT_PUBLIC_API_URL}/generate-available-patterns`,
        {
          params: {
            def_index: combinationData.def_index,
            paint_index: paintIndex,
            tier,
            max_float: maxFloat,
            api_key: userApiKey.trim(), // pass user key directly
          },
        }
      );

      if (!data.patterns || data.patterns.length === 0) {
        setNoResults(true);
      } else {
        setPatterns(data.patterns);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        console.error("Skin missing in database");
        setNoResults(true);
      } else {
        console.error(err.response?.status || err.message);
        setError("Failed to fetch patterns.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      {/* Modal Step 1 */}
      {modalStep === 1 && (
        <div className="fixed inset-0 flex items-center justify-center cs2-modal z-50">
          <div className="cs2-modal-content p-8 max-w-md w-full text-center">
            <h2 className="cs2-modal-title text-2xl mb-4">
              Welcome to CS2 Pattern Finder!
            </h2>
            <p className="cs2-text-secondary mb-4">
              Select a knife, finish, and tier to find available pattern seeds
              with listings.
            </p>
            <p className="mb-6 cs2-text-accent font-semibold">
              Please note: CSFloat has strict limits on API usage. Be mindful
              not to make too many requests in a short period. If you get timed
              out, simply wait a moment before trying again.
            </p>
            <button onClick={() => setModalStep(2)} className="cs2-button">
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal Step 2: API key input */}
      {modalStep === 2 && (
        <div className="fixed inset-0 flex items-center justify-center cs2-modal z-50">
          <div className="cs2-modal-content p-8 max-w-md w-full text-center">
            <h2 className="cs2-modal-title text-2xl mb-4">Add Your API Key</h2>
            <p className="mb-4 cs2-text-secondary text-left">
              Enter your CSFloat API key below. This key will only be used to
              make authorized requests.
            </p>
            <ol className="cs2-text-secondary text-left list-decimal list-inside mb-6 space-y-2 max-h-96 overflow-auto">
              <li>
                Go to the{" "}
                <a
                  href="https://csfloat.com/profile"
                  target="_blank"
                  rel="noreferrer"
                  className="cs2-link"
                >
                  CSFloat developers page
                </a>{" "}
                and get your API key under the "Developers" tab.
              </li>
              <li>Create a new key and copy it.</li>
              <li>Paste it in the box below.</li>
            </ol>
            <input
              type="text"
              placeholder="Enter your API key here"
              className={`w-full p-3 mb-2 rounded-md cs2-select ${
                apiKeyError ? "border-red-500" : ""
              }`}
              value={userApiKey}
              onChange={(e) => {
                setUserApiKey(e.target.value);
                if (e.target.value.trim()) setApiKeyError("");
              }}
              spellCheck={false}
              autoComplete="off"
            />
            {apiKeyError && (
              <p className="text-red-500 text-sm mb-4">{apiKeyError}</p>
            )}
            <button onClick={handleCloseApiKeyModal} className="cs2-button">
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Main UI */}
      <div className="min-h-screen p-6 flex flex-col items-center">
        <div className="max-w-4xl w-full">
          {/* Enhanced Header */}
          <div className="cs2-header fade-in">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img
                src="/chewie.png"
                alt="Chewie the Hamster"
                className="w-16 h-16 pixelated"
              />
              <h1>CS2 Pattern Finder</h1>
            </div>
            <p className="cs2-text-secondary text-lg mt-2">
              Discover rare knife patterns with real-time CSFloat listings
            </p>
          </div>

          {/* Enhanced Search Form */}
          <div className="cs2-card p-8 slide-up">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <select
                  value={knife}
                  onChange={(e) => setKnife(e.target.value)}
                  className="cs2-select p-4 w-full"
                >
                  <option value="" disabled hidden>
                    🗡️ Knife type
                  </option>
                  {availableOptions
                    ? availableOptions.knives.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))
                    : []}
                </select>
                {availableOptions && (
                  <p className="text-xs text-gray-400 mt-1">
                    {availableOptions.knives.length} knives available
                  </p>
                )}
              </div>

              <div>
                <select
                  value={finish}
                  onChange={(e) => setFinish(e.target.value)}
                  className="cs2-select p-4 w-full"
                  disabled={!knife}
                >
                  <option value="" disabled hidden>
                    ✨ Finish
                  </option>
                  {availableFinishes.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                {knife && (
                  <p className="text-xs text-gray-400 mt-1">
                    {availableFinishes.length} finishes available for {knife}
                  </p>
                )}
              </div>

              <div>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="cs2-select p-4 w-full"
                  disabled={!knife || !finish}
                >
                  <option value="" disabled hidden>
                    🏆 Tier
                  </option>
                  {availableTiers.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                {knife && finish && (
                  <p className="text-xs text-gray-400 mt-1">
                    {availableTiers.length} tiers available for {knife} {finish}
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={fetchPatterns}
              disabled={loading || !knife || !finish || !tier}
              className="cs2-button w-full md:w-auto flex items-center gap-2"
            >
              {loading ? (
                <span className="cs2-loading">Searching Patterns...</span>
              ) : (
                <>
                  <img
                    src="/chewie_search.png"
                    alt="Chewie Searching"
                    className="w-12 h-12 pixelated"
                  />
                  Find Patterns
                </>
              )}
            </button>

            {error && <div className="cs2-error mt-4">{error}</div>}
          </div>

          {/* Enhanced Results Section */}
          <div className="mt-8 space-y-6">
            {noResults && (
              <div className="cs2-card p-8 text-center">
                <p className="cs2-text-accent text-xl font-semibold">
                  ⚠️ No patterns found for this combination
                </p>
                <p className="cs2-text-secondary mt-2">
                  This knife/finish/tier combination does not exist in our
                  database.
                </p>
              </div>
            )}

            {!loading && hasSearched && !noResults && patterns.length === 0 && (
              <div className="cs2-card p-8 text-center">
                <p className="cs2-text-secondary">
                  Combination missing in database.
                </p>
              </div>
            )}

            {patterns.map((pattern, index) => (
              <div
                key={pattern.seed}
                className="cs2-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="cs2-card-header">
                  <h2 className="cs2-card-title">
                    🎯 Pattern Seed: {pattern.seed}
                  </h2>
                  <a
                    href={pattern.queryUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="cs2-link inline-block mt-2"
                  >
                    🔗 View pattern on CSFloat
                  </a>
                </div>

                <div className="p-6 space-y-4">
                  {pattern.listings.map((item) => (
                    <div key={item.id} className="cs2-listing-card">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                        <div className="space-y-2">
                          <p>
                            <span className="cs2-text-primary font-semibold">
                              Name:
                            </span>{" "}
                            <span className="cs2-text-accent">
                              {item.item.market_hash_name}
                            </span>
                          </p>
                          <p>
                            <span className="cs2-text-primary font-semibold">
                              Description:
                            </span>{" "}
                            <span className="cs2-text-secondary">
                              {item.description || "No description available"}
                            </span>
                          </p>
                          <p>
                            <span className="cs2-text-primary font-semibold">
                              Float:
                            </span>{" "}
                            <span className="cs2-text-secondary">
                              {item.item.float_value.toFixed(6)}
                            </span>
                          </p>
                        </div>
                        <div className="space-y-2">
                          <p>
                            <span className="cs2-text-primary font-semibold">
                              Price:
                            </span>{" "}
                            <span className="cs2-text-price">
                              ${(item.price / 100).toFixed(2)} USD
                            </span>
                          </p>
                          <p>
                            <span className="cs2-text-primary font-semibold">
                              Overpay:
                            </span>{" "}
                            <span className="cs2-text-overpay">
                              {item.price - item.reference.base_price > 0
                                ? `$${(
                                    (item.price - item.reference.base_price) /
                                    100
                                  ).toFixed(2)} USD`
                                : "Market price"}
                            </span>
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2 mt-4">
                            <a
                              href={"https://csfloat.com/item/" + item.id}
                              target="_blank"
                              rel="noreferrer"
                              className="cs2-link text-sm"
                            >
                              🛒 View on CSFloat
                            </a>
                            <a
                              href={item.item.inspect_link}
                              target="_blank"
                              rel="noreferrer"
                              className="cs2-link text-sm"
                            >
                              🎮 Inspect in Game
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Preserved Image Display Section */}
                      <div className="image-section">
                        <div className="flip-container">
                          <div
                            className={`flip-card ${
                              flippedItems.has(item.id) ? "flipped" : ""
                            }`}
                          >
                            {/* Front side (playside) */}
                            <div className="flip-card-front">
                              <img
                                src={
                                  "https://s.csfloat.com/m/" +
                                  item.item.cs2_screenshot_id +
                                  "/playside.png"
                                }
                                alt="Playside"
                                className="flip-image"
                              />
                            </div>

                            {/* Back side (backside) */}
                            <div className="flip-card-back">
                              <img
                                src={
                                  "https://s.csfloat.com/m/" +
                                  item.item.cs2_screenshot_id +
                                  "/backside.png"
                                }
                                alt="Backside"
                                className="flip-image"
                              />
                            </div>
                          </div>
                          {/* Invisible placeholder to maintain height */}
                          <img
                            src={
                              "https://s.csfloat.com/m/" +
                              item.item.cs2_screenshot_id +
                              "/playside.png"
                            }
                            alt=""
                            className="flip-placeholder"
                          />
                          {/* Label showing current side */}
                          <p className="side-label">
                            {flippedItems.has(item.id)
                              ? "Backside"
                              : "Frontside"}
                          </p>
                        </div>

                        {/* Button to toggle flip */}
                        <button
                          onClick={() => toggleFlip(item.id)}
                          className="flip-button"
                        >
                          🔄 Flip
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
